import { persianToEnglish } from ".";

export type PersianNumberConfig = {
  value: string | number;
  includeRial?: boolean; // Add "ریال" at the end
  includeToman?: boolean; // Add "تومان" at the end
};

// Persian word mappings
const ONES = [
  "",
  "یک",
  "دو",
  "سه",
  "چهار",
  "پنج",
  "شش",
  "هفت",
  "هشت",
  "نه",
  "ده",
  "یازده",
  "دوازده",
  "سیزده",
  "چهارده",
  "پانزده",
  "شانزده",
  "هفده",
  "هجده",
  "نوزده",
];

const TENS = [
  "",
  "",
  "بیست",
  "سی",
  "چهل",
  "پنجاه",
  "شصت",
  "هفتاد",
  "هشتاد",
  "نود",
];

const HUNDREDS = [
  "",
  "صد",
  "دویست",
  "سیصد",
  "چهارصد",
  "پانصد",
  "ششصد",
  "هفتصد",
  "هشتصد",
  "نهصد",
];

const SCALE = ["", "هزار", "میلیون", "میلیارد", "بیلیون", "بیلیارد", "تریلیون"];

// Convert three-digit groups to words
const convertThreeDigits = (num: number): string => {
  if (num === 0) return "";

  let result = "";
  const hundreds = Math.floor(num / 100);
  const remainder = num % 100;

  if (hundreds > 0) {
    result += HUNDREDS[hundreds];
  }

  if (remainder > 0) {
    if (result) result += " و ";

    if (remainder < 20) {
      result += ONES[remainder];
    } else {
      const tens = Math.floor(remainder / 10);
      const ones = remainder % 10;

      result += TENS[tens];
      if (ones > 0) {
        result += " و " + ONES[ones];
      }
    }
  }

  return result;
};

// Main conversion function
export const numberToPersianWords = ({
  value,
  includeRial = false,
  includeToman = false,
}: PersianNumberConfig): string => {
  // Convert input to string and clean it
  let numStr = typeof value === "number" ? value.toString() : value.toString();

  // Convert Persian/Arabic numerals to English for processing
  numStr = persianToEnglish(numStr);

  // Remove any non-digit characters except decimal point and negative sign
  numStr = numStr.replace(/[^\d.-]/g, "");

  // Handle empty or invalid input
  if (!numStr || numStr === "." || numStr === "-") {
    return "صفر";
  }

  // Handle negative numbers
  const isNegative = numStr.startsWith("-");
  if (isNegative) {
    numStr = numStr.substring(1);
  }

  // Split decimal part
  const [integerPart, decimalPart] = numStr.split(".");

  // Convert integer part
  let result = convertIntegerPart(integerPart || "0");

  // Handle decimal part
  if (decimalPart && parseInt(decimalPart) > 0) {
    const decimalWords = convertDecimalPart(decimalPart);
    if (decimalWords) {
      result += " ممیز " + decimalWords;
    }
  }

  // Add negative prefix
  if (isNegative && result !== "صفر") {
    result = "منفی " + result;
  }

  // Add currency suffix
  if (includeRial) {
    result += " ریال";
  } else if (includeToman) {
    result += " تومان";
  }

  return result.trim();
};

// Convert integer part to words
const convertIntegerPart = (integerStr: string): string => {
  const num = parseInt(integerStr);

  if (num === 0) return "صفر";

  // Split number into groups of three digits
  const groups: number[] = [];
  let tempNum = num;

  while (tempNum > 0) {
    groups.unshift(tempNum % 1000);
    tempNum = Math.floor(tempNum / 1000);
  }

  let result = "";

  for (let i = 0; i < groups.length; i++) {
    const group = groups[i];
    const scaleIndex = groups.length - 1 - i;

    if (group > 0) {
      const groupWords = convertThreeDigits(group);

      if (result) result += " و ";
      result += groupWords;

      if (scaleIndex > 0) {
        result += " " + SCALE[scaleIndex];
      }
    }
  }

  return result;
};

// Convert decimal part to words
const convertDecimalPart = (decimalStr: string): string => {
  // Remove trailing zeros
  decimalStr = decimalStr.replace(/0+$/, "");

  if (!decimalStr) return "";

  const decimalNum = parseInt(decimalStr);
  return convertIntegerPart(decimalNum.toString());
};
